import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Edit3, X, Sparkles, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { AutonomyPill } from "@/components/nadia/Badges";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { listActions, decideAction, runOptimizer } from "@/lib/api";

const RISK_STYLES = {
  LOW: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  MEDIUM: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  HIGH: "bg-rose-500/10 text-rose-300 border-rose-500/20",
};

export default function AzioniNadia() {
  const [actions, setActions] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => listActions().then(setActions).catch(() => setActions([]));
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      await decideAction(id, decision);
      toast.success(
        decision === "approve"
          ? "Azione approvata"
          : decision === "reject"
          ? "Azione scartata"
          : "Segnalata per modifica"
      );
      load();
    } catch {
      toast.error("Errore");
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    try {
      await runOptimizer();
      toast.success("Nadia ha analizzato la campagna");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  if (!actions) return <Skeleton className="h-96 bg-white/5" />;

  return (
    <>
      <PageHeader
        eyebrow="Azioni di Nadia"
        title="Cosa vuole fare Nadia"
        subtitle="Ogni azione ha una spiegazione, un rischio e un effetto stimato. Tu decidi."
        actions={
          <Button
            onClick={generate}
            variant="outline"
            className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
            data-testid="generate-action-button"
          >
            <Sparkles size={15} className="mr-2" /> Chiedi nuova analisi
          </Button>
        }
      />

      <div className="space-y-4">
        {actions.map((a) => (
          <div key={a.id} className="nadia-card p-5 sm:p-6" data-testid={`action-card-${a.id}`}>
            <div className="flex items-start gap-4">
              <NadiaAvatar size="sm" />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <AutonomyPill level={a.autonomy_level || "CONSIGLIA"} />
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${RISK_STYLES[a.risk] || RISK_STYLES.LOW}`}>
                    {a.risk === "HIGH" ? <AlertTriangle size={10} className="mr-1 inline" /> : null}
                    Rischio {a.risk}
                  </span>
                  {a.status !== "pending" && (
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-700/60 text-slate-300 border border-white/10">
                      {a.status}
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-50 mb-1.5">{a.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-3">{a.reason}</p>
                <div className="text-xs text-slate-400 bg-white/[0.02] border border-white/5 rounded-xl px-3 py-2 mb-4">
                  <span className="text-cyan-300 font-semibold">Effetto atteso · </span>
                  {a.expected_effect}
                </div>
                {a.status === "pending" && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => decide(a.id, "approve")}
                      disabled={busy === a.id}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                      data-testid={`approve-${a.id}`}
                    >
                      <Check size={14} className="mr-1.5" /> Approva
                    </Button>
                    <Button
                      onClick={() => decide(a.id, "modify")}
                      disabled={busy === a.id}
                      variant="outline"
                      className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
                      data-testid={`modify-${a.id}`}
                    >
                      <Edit3 size={14} className="mr-1.5" /> Modifica
                    </Button>
                    <Button
                      onClick={() => decide(a.id, "reject")}
                      disabled={busy === a.id}
                      variant="outline"
                      className="border-white/10 text-slate-400 hover:text-rose-300 hover:border-rose-400/40 bg-transparent"
                      data-testid={`reject-${a.id}`}
                    >
                      <X size={14} className="mr-1.5" /> Non fare
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {actions.length === 0 && (
          <div className="nadia-card p-8 text-center text-slate-400 text-sm">
            Nessuna azione in attesa. Nadia sta monitorando.
          </div>
        )}
      </div>
    </>
  );
}
